import { useMemo } from "react";
import {
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  Build as BuildIcon,
  AddCircleOutline as AddIcon,
  Edit as EditIcon,
  Schedule as ScheduleIcon,
  Speed as SpeedIcon,
  Power as OnlineIcon,
  PowerOff as OfflineIcon,
} from "@mui/icons-material";
import type { MenuOptionType } from "@/controls/contextMenu/models/MenuOptionType";
import {
  PART_IDS,
  isSpareContainer,
  isStrandContainer,
  isGroupType,
  PART_TYPE_IDS,
  type CasterTreeRow,
} from "../models";

interface UseCasterContextMenuProps {
  onInstall: (row: CasterTreeRow) => void;
  onUninstall: (row: CasterTreeRow) => void;
  onRepair: (row: CasterTreeRow) => void;
  onNewSparePart: (row: CasterTreeRow) => void;
  onEditPart: (row: CasterTreeRow) => void;
  onSetInitialMetric: (row: CasterTreeRow) => void;
  onCorrectDates: (row: CasterTreeRow) => void;
  onSetOnline: (row: CasterTreeRow) => void;
  onSetOffline: (row: CasterTreeRow) => void;
}

/**
 * Top-level containers that should not show context menu.
 * Includes Caster-Online, Caster-Offline, and Mold Spares.
 * Note: Strand containers ARE allowed context menu (Set Online).
 */
const EXCLUDED_CONTAINERS: number[] = [
  PART_IDS.CASTER_ONLINE,
  PART_IDS.CASTER_OFFLINE,
  PART_IDS.MOLD_SPARES,
];

export const useCasterContextMenu = ({
  onInstall,
  onUninstall,
  onRepair,
  onNewSparePart,
  onEditPart,
  onSetInitialMetric,
  onCorrectDates,
  onSetOnline,
  onSetOffline,
}: UseCasterContextMenuProps) => {
  const getMenuOptions = useMemo(
    () => (data: CasterTreeRow) => {
      const options: MenuOptionType[] = [];

      if (!data) return options;

      // ── Mold equipment rows (PartTypeId=MOLD) ──
      // These represent installed mold assemblies that can be set online/offline
      const isMoldEquipment =
        data.rowType === "equipment" && data.PartTypeId === PART_TYPE_IDS.MOLD;

      if (isMoldEquipment) {
        if (data.Status === "Online") {
          options.push({
            name: "Set Offline",
            key: "setOffline",
            onClick: () => onSetOffline(data),
            icon: <OfflineIcon fontSize="small" color="error" />,
          });
        } else {
          // Offline mold in Mold Spares → "Set Online" opens modal with strand selector
          options.push({
            name: "Set Online",
            key: "setOnline",
            onClick: () => onSetOnline(data),
            icon: <OnlineIcon fontSize="small" color="success" />,
          });
        }
        options.push({
          name: "Set Initial Metric",
          key: "setInitialMetric",
          onClick: () => onSetInitialMetric(data),
          icon: <SpeedIcon fontSize="small" color="info" />,
        });
        options.push({
          name: "Correct Dates",
          key: "correctDates",
          onClick: () => onCorrectDates(data),
          icon: <ScheduleIcon fontSize="small" color="primary" />,
        });
        return options;
      }

      // ── Strand container rows ──
      // Show "Set Online" when no mold is installed (opens modal with mold selector)
      if (data.rowType === "container" && isStrandContainer(data.ContainerId)) {
        if (!data.HasMoldInstalled) {
          options.push({
            name: "Set Online",
            key: "setOnline",
            onClick: () => onSetOnline(data),
            icon: <OnlineIcon fontSize="small" color="success" />,
          });
        }
        return options;
      }

      // ── Spares containers and spare parts ──
      if (data.rowType === "sparesContainer" || data.rowType === "sparePart") {
        const containerId = data.ContainerId ?? data.EquipmentId;
        if (EXCLUDED_CONTAINERS.includes(containerId)) {
          return options;
        }

        // Show "New Spare Part" for actual spares containers (not top-level)
        if (isSpareContainer(containerId)) {
          options.push({
            name: "New Spare Part",
            key: "newSparePart",
            onClick: () => onNewSparePart(data),
            icon: <AddIcon fontSize="small" color="primary" />,
          });
        }

        // Spare part-specific options
        if (data.rowType === "sparePart" && data.L2PartId) {
          options.push({
            name: "Edit Part",
            key: "editPart",
            onClick: () => onEditPart(data),
            icon: <EditIcon fontSize="small" color="primary" />,
          });
          options.push({
            name: "Repair",
            key: "repair",
            onClick: () => onRepair(data),
            icon: <BuildIcon fontSize="small" color="warning" />,
          });
          options.push({
            name: "Set Initial Metric",
            key: "setInitialMetric",
            onClick: () => onSetInitialMetric(data),
            icon: <SpeedIcon fontSize="small" color="info" />,
          });
          options.push({
            name: "Correct Dates",
            key: "correctDates",
            onClick: () => onCorrectDates(data),
            icon: <ScheduleIcon fontSize="small" color="primary" />,
          });
        }

        return options;
      }

      // ── Container rows (Caster-Online, Caster-Offline) ──
      // No context menu for structural containers
      if (data.rowType === "container") {
        return options;
      }

      // ── Location rows (component positions on molds) ──
      if (data.rowType !== "location") {
        return options;
      }

      // Group containers (Flanges, Collars, Seals, Foot Roll Assembly)
      // These are non-replaceable assemblies with children
      const isGroup = isGroupType(data.PartTypeId) && data.HasChildren === true;

      if (isGroup) {
        options.push({
          name: "Set Initial Metric",
          key: "setInitialMetric",
          onClick: () => onSetInitialMetric(data),
          icon: <SpeedIcon fontSize="small" color="info" />,
        });
        return options;
      }

      const hasPart = data.HasAssignedPart ?? false;
      const hasGroupChildren = data.HasChildren === true;

      if (!hasPart && !hasGroupChildren) {
        // Empty position without sub-components: allow installing a part
        options.push({
          name: "Install Part",
          key: "install",
          onClick: () => onInstall(data),
          icon: <LinkIcon fontSize="small" color="primary" />,
        });
      } else if (hasPart) {
        // Position with installed part: show management options
        options.push({
          name: "Uninstall Part",
          key: "uninstall",
          onClick: () => onUninstall(data),
          icon: <LinkOffIcon fontSize="small" color="error" />,
        });
        options.push({
          name: "Repair",
          key: "repair",
          onClick: () => onRepair(data),
          icon: <BuildIcon fontSize="small" color="warning" />,
        });
        options.push({
          name: "Edit Part",
          key: "editPart",
          onClick: () => onEditPart(data),
          icon: <EditIcon fontSize="small" color="primary" />,
        });
        options.push({
          name: "Set Initial Metric",
          key: "setInitialMetric",
          onClick: () => onSetInitialMetric(data),
          icon: <SpeedIcon fontSize="small" color="info" />,
        });
        options.push({
          name: "Correct Dates",
          key: "correctDates",
          onClick: () => onCorrectDates(data),
          icon: <ScheduleIcon fontSize="small" color="primary" />,
        });
      }

      return options;
    },
    [
      onInstall,
      onUninstall,
      onRepair,
      onNewSparePart,
      onEditPart,
      onSetInitialMetric,
      onCorrectDates,
      onSetOnline,
      onSetOffline,
    ],
  );

  return {
    getMenuOptions,
  };
};
